import React, { useState } from "react";
import { useTranslation } from 'react-i18next';

import {
  FiSave,
  FiUpload,
  FiDownload,
  FiRefreshCw,
  FiZoomIn,
  FiFileText,
  FiCode,
  FiGitBranch,
  FiFolder,
  FiChevronDown,
  FiCpu,
  FiShield,
  FiZap,
  FiMessageSquare,
  FiUploadCloud,
  FiActivity,
} from "react-icons/fi";

type Props = {
  project: {
    projectName?: string;
    name: string;
    provider: string;
    architectureType: string;
  };
  onSave: () => void;
  onLoad: () => void;
  onExport: () => void;
  onReset: () => void;
  onFitView: () => void;
  onGenerateDoc?: () => void;
  onGenerateTerraform?: () => void;
  onSaveVersion?: () => void;
  onViewVersionHistory?: () => void;
  onGenerateWithAI?: () => void;
  onReviewWithAI?: () => void;
  onFinOpsWithAI?: () => void;
  onCompareVariants?: () => void;
  onGenerateRunbook?: () => void;
  onGenerateAdr?: () => void;
  onChatWithInfra?: () => void;
  onScanCompliance?: () => void;
  onMigrationAssistant?: () => void;
  onPresalesAssistant?: () => void;
  onIncrementalUpdate?: () => void;
  onDriftDetection?: () => void;
  onContinuousOptimization?: () => void;
};

const CanvasHeader: React.FC<Props> = ({
  project,
  onSave,
  onLoad,
  onExport,
  onReset,
  onFitView,
  onGenerateDoc,
  onGenerateTerraform,
  onSaveVersion,
  onViewVersionHistory,
  onGenerateWithAI,
  onReviewWithAI,
  onFinOpsWithAI,
  onCompareVariants,
  onGenerateRunbook,
  onGenerateAdr,
  onChatWithInfra,
  onScanCompliance,
  onMigrationAssistant,
  onPresalesAssistant,
  onIncrementalUpdate,
  onDriftDetection,
  onContinuousOptimization,
}) => {
  const { t } = useTranslation('canvas');
  const [showFileMenu, setShowFileMenu] = useState(false);
  const [showDocMenu, setShowDocMenu] = useState(false);
  const [showVersionMenu, setShowVersionMenu] = useState(false);
  return (
    <header className="bg-white text-gray-800 shadow border-b">
      {/* Ligne 1 : Projet + Actions */}
      <div className="flex items-center justify-between px-6 py-3">
      <div>
        <h1 className="text-lg font-semibold">
          {project.projectName && (
            <>
              {t('header.project')} <span className="text-blue-600">{project.projectName}</span>
              <span className="text-gray-400 mx-2">•</span>
            </>
          )}
          {t('header.architecture')} <span className="text-purple-600">{project.name}</span>
        </h1>
        <p className="text-sm text-gray-500">
          {project.architectureType} • {t('header.provider')} {project.provider}
        </p>
      </div>

      {/* Actions globales */}
      <div className="flex gap-3 items-center">
        {/* Menu Fichier */}
        <div style={{ position: 'relative' }}>
          <button
            onClick={() => setShowFileMenu(!showFileMenu)}
            className="action-btn"
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 6,
              padding: '8px 12px',
              background: showFileMenu ? '#F3F4F6' : 'transparent',
            }}
            title={t('header.file.title')}
          >
            <FiFolder className="text-lg" />
            <span style={{ fontSize: 14, fontWeight: 500 }}>{t('header.file.title')}</span>
            <FiChevronDown size={14} />
          </button>
          {showFileMenu && (
            <div
              style={{
                position: 'absolute',
                top: '100%',
                left: 0,
                marginTop: 8,
                background: 'white',
                border: '1px solid #E5E7EB',
                borderRadius: 6,
                boxShadow: '0 4px 6px rgba(0,0,0,0.1)',
                zIndex: 200,
                minWidth: 200,
              }}
            >
              <button
                onClick={() => {
                  onLoad();
                  setShowFileMenu(false);
                }}
                style={{
                  width: '100%',
                  padding: '10px 16px',
                  textAlign: 'left',
                  border: 'none',
                  background: 'transparent',
                  cursor: 'pointer',
                  fontSize: 14,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 10,
                }}
                onMouseEnter={(e) => e.currentTarget.style.background = '#F3F4F6'}
                onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
              >
                <FiUpload size={16} />
                {t('header.file.load')}
              </button>
              <button
                onClick={() => {
                  onSave();
                  setShowFileMenu(false);
                }}
                style={{
                  width: '100%',
                  padding: '10px 16px',
                  textAlign: 'left',
                  border: 'none',
                  background: 'transparent',
                  cursor: 'pointer',
                  fontSize: 14,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 10,
                }}
                onMouseEnter={(e) => e.currentTarget.style.background = '#F3F4F6'}
                onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
              >
                <FiSave size={16} />
                {t('header.file.save')}
              </button>
              <button
                onClick={() => {
                  onExport();
                  setShowFileMenu(false);
                }}
                style={{
                  width: '100%',
                  padding: '10px 16px',
                  textAlign: 'left',
                  border: 'none',
                  background: 'transparent',
                  cursor: 'pointer',
                  fontSize: 14,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 10,
                }}
                onMouseEnter={(e) => e.currentTarget.style.background = '#F3F4F6'}
                onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
              >
                <FiDownload size={16} />
                {t('header.file.export')}
              </button>
            </div>
          )}
        </div>

        {/* Séparateur */}
        <div style={{ width: 1, height: 24, background: '#E5E7EB' }} />

        {/* Versions */}
        {(onSaveVersion || onViewVersionHistory) && (
          <div style={{ position: 'relative' }}>
            <button
              onClick={() => setShowVersionMenu(!showVersionMenu)}
              className="action-btn"
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 6,
                padding: '8px 12px',
                background: showVersionMenu ? '#F3F4F6' : 'transparent',
              }}
              title={t('header.versions.management')}
            >
              <FiGitBranch className="text-lg" />
              <span style={{ fontSize: 14, fontWeight: 500 }}>{t('header.versions.title')}</span>
              <FiChevronDown size={14} />
            </button>
            {showVersionMenu && (
              <div
                style={{
                  position: 'absolute',
                  top: '100%',
                  right: 0,
                  marginTop: 8,
                  background: 'white',
                  border: '1px solid #E5E7EB',
                  borderRadius: 6,
                  boxShadow: '0 4px 6px rgba(0,0,0,0.1)',
                  zIndex: 200,
                  minWidth: 200,
                }}
              >
                {onSaveVersion && (
                  <button
                    onClick={() => {
                      onSaveVersion();
                      setShowVersionMenu(false);
                    }}
                    style={{
                      width: '100%',
                      padding: '10px 16px',
                      textAlign: 'left',
                      border: 'none',
                      background: 'transparent',
                      cursor: 'pointer',
                      fontSize: 14,
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.background = '#F3F4F6'}
                    onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
                  >
                    {t('header.versions.create')}
                  </button>
                )}
                {onViewVersionHistory && (
                  <button
                    onClick={() => {
                      onViewVersionHistory();
                      setShowVersionMenu(false);
                    }}
                    style={{
                      width: '100%',
                      padding: '10px 16px',
                      textAlign: 'left',
                      border: 'none',
                      background: 'transparent',
                      cursor: 'pointer',
                      fontSize: 14,
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.background = '#F3F4F6'}
                    onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
                  >
                    {t('header.versions.viewHistory')}
                  </button>
                )}
              </div>
            )}
          </div>
        )}

        {/* Documentation */}
        {(onGenerateDoc || onGenerateTerraform || onReviewWithAI || onFinOpsWithAI || onCompareVariants || onGenerateRunbook || onGenerateAdr || onChatWithInfra || onScanCompliance || onMigrationAssistant || onPresalesAssistant || onIncrementalUpdate || onDriftDetection || onContinuousOptimization) && (
          <div style={{ position: 'relative' }}>
            <button
              onClick={() => setShowDocMenu(!showDocMenu)}
              className="action-btn"
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 6,
                padding: '8px 12px',
                background: showDocMenu ? '#F3F4F6' : 'transparent',
              }}
              title={t('header.documentation.generate')}
            >
              <FiFileText className="text-lg" />
              <span style={{ fontSize: 14, fontWeight: 500 }}>{t('header.documentation.title')}</span>
              <FiChevronDown size={14} />
            </button>
            {showDocMenu && (
              <div
                style={{
                  position: 'absolute',
                  top: '100%',
                  right: 0,
                  marginTop: 8,
                  background: 'white',
                  border: '1px solid #E5E7EB',
                  borderRadius: 6,
                  boxShadow: '0 4px 6px rgba(0,0,0,0.1)',
                  zIndex: 200,
                  minWidth: 200,
                }}
              >
                {onGenerateDoc && (
                  <button
                    onClick={() => { onGenerateDoc(); setShowDocMenu(false); }}
                    style={{
                      width: '100%',
                      padding: '10px 16px',
                      textAlign: 'left',
                      border: 'none',
                      background: 'transparent',
                      cursor: 'pointer',
                      fontSize: 14,
                      display: 'flex',
                      alignItems: 'center',
                      gap: 10,
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.background = '#EFF6FF'}
                    onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
                  >
                    <FiCpu size={15} color="#1e40af" />
                    <span style={{ color: '#1e40af', fontWeight: 500 }}>Générer Documentation IA</span>
                  </button>
                )}
                {onGenerateTerraform && (
                  <button
                    onClick={() => { onGenerateTerraform(); setShowDocMenu(false); }}
                    style={{
                      width: '100%',
                      padding: '10px 16px',
                      textAlign: 'left',
                      border: 'none',
                      background: 'transparent',
                      cursor: 'pointer',
                      fontSize: 14,
                      display: 'flex',
                      alignItems: 'center',
                      gap: 10,
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.background = '#F3F4F6'}
                    onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
                  >
                    <FiCode size={15} />
                    Terraform HCL (IA)
                  </button>
                )}
                {onReviewWithAI && (
                  <button onClick={() => { onReviewWithAI(); setShowDocMenu(false); }}
                    style={{ width:'100%', padding:'10px 16px', textAlign:'left', border:'none', background:'transparent', cursor:'pointer', fontSize:14, display:'flex', alignItems:'center', gap:10 }}
                    onMouseEnter={(e) => e.currentTarget.style.background = '#F5F3FF'}
                    onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}>
                    <FiShield size={15} color="#7c3aed" />
                    <span style={{ color: '#7c3aed', fontWeight: 500 }}>Revue IA (Sécurité)</span>
                  </button>
                )}
                {onFinOpsWithAI && (
                  <button onClick={() => { onFinOpsWithAI(); setShowDocMenu(false); }}
                    style={{ width:'100%', padding:'10px 16px', textAlign:'left', border:'none', background:'transparent', cursor:'pointer', fontSize:14, display:'flex', alignItems:'center', gap:10 }}
                    onMouseEnter={(e) => e.currentTarget.style.background = '#F0FDF4'}
                    onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}>
                    <FiZap size={15} color="#16a34a" />
                    <span style={{ color: '#16a34a', fontWeight: 500 }}>Recommandations FinOps</span>
                  </button>
                )}
                {onCompareVariants && (
                  <button onClick={() => { onCompareVariants(); setShowDocMenu(false); }}
                    style={{ width:'100%', padding:'10px 16px', textAlign:'left', border:'none', background:'transparent', cursor:'pointer', fontSize:14, display:'flex', alignItems:'center', gap:10 }}
                    onMouseEnter={(e) => e.currentTarget.style.background = '#EEF2FF'}
                    onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}>
                    <FiGitBranch size={15} color="#4f46e5" />
                    <span style={{ color: '#4f46e5', fontWeight: 500 }}>Comparer variantes</span>
                  </button>
                )}
                {onGenerateRunbook && (
                  <button onClick={() => { onGenerateRunbook(); setShowDocMenu(false); }}
                    style={{ width:'100%', padding:'10px 16px', textAlign:'left', border:'none', background:'transparent', cursor:'pointer', fontSize:14, display:'flex', alignItems:'center', gap:10 }}
                    onMouseEnter={(e) => e.currentTarget.style.background = '#FFF7ED'}
                    onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}>
                    <FiFileText size={15} color="#ea580c" />
                    <span style={{ color: '#ea580c', fontWeight: 500 }}>Générer Runbook</span>
                  </button>
                )}
                {onGenerateAdr && (
                  <button onClick={() => { onGenerateAdr(); setShowDocMenu(false); }}
                    style={{ width:'100%', padding:'10px 16px', textAlign:'left', border:'none', background:'transparent', cursor:'pointer', fontSize:14, display:'flex', alignItems:'center', gap:10 }}
                    onMouseEnter={(e) => e.currentTarget.style.background = '#F0F9FF'}
                    onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}>
                    <FiCode size={15} color="#0284c7" />
                    <span style={{ color: '#0284c7', fontWeight: 500 }}>Générer ADR</span>
                  </button>
                )}
                {onChatWithInfra && (
                  <button onClick={() => { onChatWithInfra(); setShowDocMenu(false); }}
                    style={{ width:'100%', padding:'10px 16px', textAlign:'left', border:'none', background:'transparent', cursor:'pointer', fontSize:14, display:'flex', alignItems:'center', gap:10 }}
                    onMouseEnter={(e) => e.currentTarget.style.background = '#FDF4FF'}
                    onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}>
                    <FiMessageSquare size={15} color="#9333ea" />
                    <span style={{ color: '#9333ea', fontWeight: 500 }}>Chat Infrastructure</span>
                  </button>
                )}
                {onScanCompliance && (
                  <button onClick={() => { onScanCompliance(); setShowDocMenu(false); }}
                    style={{ width:'100%', padding:'10px 16px', textAlign:'left', border:'none', background:'transparent', cursor:'pointer', fontSize:14, display:'flex', alignItems:'center', gap:10 }}
                    onMouseEnter={(e) => e.currentTarget.style.background = '#EEF2FF'}
                    onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}>
                    <FiShield size={15} color="#4f46e5" />
                    <span style={{ color: '#4f46e5', fontWeight: 500 }}>Scan de conformité</span>
                  </button>
                )}
                {onMigrationAssistant && (
                  <button onClick={() => { onMigrationAssistant(); setShowDocMenu(false); }}
                    style={{ width:'100%', padding:'10px 16px', textAlign:'left', border:'none', background:'transparent', cursor:'pointer', fontSize:14, display:'flex', alignItems:'center', gap:10 }}
                    onMouseEnter={(e) => e.currentTarget.style.background = '#F0FDFA'}
                    onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}>
                    <FiUploadCloud size={15} color="#0d9488" />
                    <span style={{ color: '#0d9488', fontWeight: 500 }}>Assistant Migration</span>
                  </button>
                )}
                {onPresalesAssistant && (
                  <button onClick={() => { onPresalesAssistant(); setShowDocMenu(false); }}
                    style={{ width:'100%', padding:'10px 16px', textAlign:'left', border:'none', background:'transparent', cursor:'pointer', fontSize:14, display:'flex', alignItems:'center', gap:10 }}
                    onMouseEnter={(e) => e.currentTarget.style.background = '#F5F3FF'}
                    onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}>
                    <FiFileText size={15} color="#7c3aed" />
                    <span style={{ color: '#7c3aed', fontWeight: 500 }}>Proposition Commerciale</span>
                  </button>
                )}
                {onIncrementalUpdate && (
                  <button onClick={() => { onIncrementalUpdate(); setShowDocMenu(false); }}
                    style={{ width:'100%', padding:'10px 16px', textAlign:'left', border:'none', background:'transparent', cursor:'pointer', fontSize:14, display:'flex', alignItems:'center', gap:10 }}
                    onMouseEnter={(e) => e.currentTarget.style.background = '#FFFBEB'}
                    onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}>
                    <FiZap size={15} color="#d97706" />
                    <span style={{ color: '#d97706', fontWeight: 500 }}>Mise à jour incrémentale</span>
                  </button>
                )}
                {onDriftDetection && (
                  <button onClick={() => { onDriftDetection(); setShowDocMenu(false); }}
                    style={{ width:'100%', padding:'10px 16px', textAlign:'left', border:'none', background:'transparent', cursor:'pointer', fontSize:14, display:'flex', alignItems:'center', gap:10 }}
                    onMouseEnter={(e) => e.currentTarget.style.background = '#EEF2FF'}
                    onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}>
                    <FiActivity size={15} color="#4f46e5" />
                    <span style={{ color: '#4f46e5', fontWeight: 500 }}>Drift Detection</span>
                  </button>
                )}
                {onContinuousOptimization && (
                  <button onClick={() => { onContinuousOptimization(); setShowDocMenu(false); }}
                    style={{ width:'100%', padding:'10px 16px', textAlign:'left', border:'none', background:'transparent', cursor:'pointer', fontSize:14, display:'flex', alignItems:'center', gap:10 }}
                    onMouseEnter={(e) => e.currentTarget.style.background = '#EEF2FF'}
                    onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}>
                    <FiZap size={15} color="#4f46e5" />
                    <span style={{ color: '#4f46e5', fontWeight: 500 }}>Optimisation continue</span>
                  </button>
                )}
              </div>
            )}
          </div>
        )}

        {/* Séparateur */}
        <div style={{ width: 1, height: 24, background: '#E5E7EB' }} />

        {/* Générer avec IA */}
        {onGenerateWithAI && (
          <button
            onClick={onGenerateWithAI}
            className="action-btn"
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 6,
              padding: '8px 12px',
              background: 'linear-gradient(135deg, #3B82F6, #8B5CF6)',
              color: 'white',
              borderRadius: 8,
              border: 'none',
              cursor: 'pointer',
            }}
            title="Générer une architecture avec l'IA"
          >
            <FiCpu size={16} />
            <span style={{ fontSize: 14, fontWeight: 600 }}>Générer avec IA</span>
          </button>
        )}

        {/* Séparateur */}
        <div style={{ width: 1, height: 24, background: '#E5E7EB' }} />

        {/* Fit View */}
        <button
          onClick={onFitView}
          className="action-btn"
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 6,
            padding: '8px 12px',
          }}
          title={t('header.fitViewTitle')}
        >
          <FiZoomIn className="text-lg" />
          <span style={{ fontSize: 14, fontWeight: 500 }}>{t('header.fitView')}</span>
        </button>
      </div>
      </div>{/* fin ligne 1 */}

      {/* Ligne 2 : Toolbar canvas — rempli par FlowEditor via portal */}
      <div
        id="canvas-toolbar-slot"
        style={{
          borderTop: '1px solid #f3f4f6',
          padding: '4px 16px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 4,
          minHeight: 40,
        }}
      />
    </header>
  );
};

export default CanvasHeader;
